import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ChevronLeft,
  ChevronRight,
  RotateCw,
  Maximize,
  PanelLeftClose,
  PanelLeftOpen,
  Minus,
  Plus,
  Loader2,
} from "lucide-react";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PdfViewerProps {
  url: string;
  onLoadComplete?: () => void;
  onError?: () => void;
}

const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4.0;

export const PdfViewer = ({ url, onLoadComplete, onError }: PdfViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [rotation, setRotation] = useState<number>(0);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fitMode, setFitMode] = useState<"width" | "page" | "custom">("page");
  const [pageInputValue, setPageInputValue] = useState<string>("1");

  const mainContentRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Use refs for container dimensions to avoid re-render on every resize tick
  const containerDimsRef = useRef({ width: 0, height: 0 });
  // Only trigger a single re-render once dimensions are first available
  const [dimsReady, setDimsReady] = useState(false);
  // Force update counter for when dimensions actually matter (zoom/fit changes)
  const [, setForceUpdate] = useState(0);

  // Stable refs for callbacks to avoid re-render loops
  const onLoadCompleteRef = useRef(onLoadComplete);
  onLoadCompleteRef.current = onLoadComplete;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Memoize the file prop so react-pdf doesn't refetch on every render
  const fileSource = useMemo(() => ({ url }), [url]);

  // Observe container size - use ref to avoid re-renders on every resize tick
  useEffect(() => {
    const container = mainContentRef.current;
    if (!container) return;

    let rafId: number | null = null;

    const observer = new ResizeObserver((entries) => {
      // Cancel any pending update
      if (rafId) cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        for (const entry of entries) {
          const w = entry.contentRect.width;
          const h = entry.contentRect.height;
          containerDimsRef.current = { width: w, height: h };

          // Only trigger re-render once when dims first become available
          if (w > 0 && h > 0 && !dimsReady) {
            setDimsReady(true);
          }
        }
      });
    });

    observer.observe(container);
    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [dimsReady]);

  // ─── Document callbacks (stable - no prop dependencies) ───
  const onDocumentLoadSuccess = useCallback(
    ({ numPages: total }: { numPages: number }) => {
      setNumPages(total);
      setIsLoading(false);
      onLoadCompleteRef.current?.();
    },
    []
  );

  const onDocumentLoadError = useCallback(() => {
    setIsLoading(false);
    onErrorRef.current?.();
  }, []);

  // ─── Page navigation ───
  const goToPage = useCallback(
    (page: number) => {
      const target = Math.max(1, Math.min(page, numPages));
      setCurrentPage(target);
      setPageInputValue(String(target));

      // Scroll the page into view in the main viewport
      const pageEl = pageRefs.current.get(target);
      if (pageEl) {
        pageEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [numPages]
  );

  const goToPrevPage = useCallback(
    () => goToPage(currentPage - 1),
    [currentPage, goToPage]
  );
  const goToNextPage = useCallback(
    () => goToPage(currentPage + 1),
    [currentPage, goToPage]
  );

  // ─── Zoom ───
  const zoomIn = useCallback(() => {
    setScale((s) => Math.min(s + ZOOM_STEP, MAX_ZOOM));
    setFitMode("custom");
  }, []);

  const zoomOut = useCallback(() => {
    setScale((s) => Math.max(s - ZOOM_STEP, MIN_ZOOM));
    setFitMode("custom");
  }, []);

  const fitToPage = useCallback(() => {
    setFitMode("page");
    // Force a re-render so pages pick up current container dims from ref
    setForceUpdate((c) => c + 1);
  }, []);

  // ─── Rotate ───
  const rotate = useCallback(() => {
    setRotation((r) => (r + 90) % 360);
  }, []);

  // ─── Scroll-based page tracking ───
  const handleMainScroll = useCallback(() => {
    const container = mainContentRef.current;
    if (!container) return;

    const containerTop = container.scrollTop;
    const containerMid = containerTop + container.clientHeight / 2;

    let closestPage = currentPage;
    let closestDist = Infinity;

    pageRefs.current.forEach((el, pageNum) => {
      const elTop = el.offsetTop;
      const elMid = elTop + el.offsetHeight / 2;
      const dist = Math.abs(containerMid - elMid);
      if (dist < closestDist) {
        closestDist = dist;
        closestPage = pageNum;
      }
    });

    if (closestPage !== currentPage) {
      setCurrentPage(closestPage);
      setPageInputValue(String(closestPage));
    }
  }, [currentPage]);

  // Percentage display
  const displayZoom = Math.round((fitMode === "custom" ? scale : scale) * 100);

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const val = parseInt(pageInputValue, 10);
      if (!isNaN(val)) goToPage(val);
    }
  };

  const handlePageInputBlur = () => {
    const val = parseInt(pageInputValue, 10);
    if (!isNaN(val)) {
      goToPage(val);
    } else {
      setPageInputValue(String(currentPage));
    }
  };

  // ─── Thumbnail click ───
  const handleThumbnailClick = useCallback(
    (page: number) => {
      goToPage(page);
    },
    [goToPage]
  );

  // ─── Compute page dimensions from ref (no state dependency) ───
  const getPageWidth = () => {
    const dims = containerDimsRef.current;
    if (fitMode === "width") {
      return dims.width - (showSidebar ? 180 : 0) - 40;
    }
    return undefined;
  };

  const getPageHeight = () => {
    const dims = containerDimsRef.current;
    if (fitMode === "page" && dims.height > 0) {
      return dims.height - 30;
    }
    return undefined;
  };

  // Check if we're ready to render pages
  const canRenderPages = dimsReady && numPages > 0;

  return (
    <div className="flex flex-col w-full h-full bg-muted overflow-hidden">
      {/* ═══ Toolbar ═══ */}
      <div className="flex items-center justify-between px-2 py-1.5 bg-card border-b border-border shrink-0 select-none min-h-[40px]">
        {/* Left group: Sidebar toggle */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSidebar((s) => !s)}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={showSidebar ? "Hide sidebar" : "Show sidebar"}
          >
            {showSidebar ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeftOpen className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Center group: Page navigation + Zoom + Rotate + Fit */}
        <div className="flex items-center gap-1">
          {/* ── Page Navigation ── */}
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:text-muted-foreground/30 disabled:hover:bg-transparent transition-colors"
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center text-foreground text-xs">
            <input
              type="text"
              value={pageInputValue}
              onChange={(e) => setPageInputValue(e.target.value)}
              onKeyDown={handlePageInputKeyDown}
              onBlur={handlePageInputBlur}
              className="w-8 text-center bg-muted border border-border rounded px-1 py-0.5 text-foreground text-xs focus:outline-none focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="mx-1 text-muted-foreground">/</span>
            <span className="text-muted-foreground">{numPages}</span>
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage >= numPages}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:text-muted-foreground/30 disabled:hover:bg-transparent transition-colors"
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-border mx-1" />

          {/* ── Zoom Controls ── */}
          <button
            onClick={zoomOut}
            disabled={scale <= MIN_ZOOM}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:text-muted-foreground/30 disabled:hover:bg-transparent transition-colors"
            title="Zoom out"
          >
            <Minus className="h-4 w-4" />
          </button>

          <span className="text-muted-foreground text-xs min-w-[36px] text-center">
            {displayZoom}%
          </span>

          <button
            onClick={zoomIn}
            disabled={scale >= MAX_ZOOM}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:text-muted-foreground/30 disabled:hover:bg-transparent transition-colors"
            title="Zoom in"
          >
            <Plus className="h-4 w-4" />
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-border mx-1" />

          {/* ── Fit to page ── */}
          <button
            onClick={fitToPage}
            className={`p-1.5 rounded hover:bg-muted transition-colors ${
              fitMode === "page"
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
            title="Fit to page"
          >
            <Maximize className="h-4 w-4" />
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-border mx-1" />

          {/* ── Rotate ── */}
          <button
            onClick={rotate}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Rotate clockwise"
          >
            <RotateCw className="h-4 w-4" />
          </button>
        </div>

        {/* Right group: empty spacer for balance */}
        <div className="w-8" />
      </div>

      {/* ═══ Body: Sidebar + Main Content ═══ */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── Sidebar (Thumbnails) ── */}
        {showSidebar && (
          <div className="w-[180px] bg-card border-r border-border overflow-y-auto shrink-0">
            <div className="py-2">
              <Document file={fileSource} loading={null}>
                {Array.from({ length: numPages }, (_, i) => i + 1).map(
                  (pageNum) => (
                    <div
                      key={`thumb-${pageNum}`}
                      className={`mx-2 mb-2 cursor-pointer rounded-md overflow-hidden transition-all ${
                        pageNum === currentPage
                          ? "ring-2 ring-primary ring-offset-1 ring-offset-card"
                          : "hover:ring-1 hover:ring-border"
                      }`}
                      onClick={() => handleThumbnailClick(pageNum)}
                    >
                      <Page
                        pageNumber={pageNum}
                        width={140}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                      <div className="text-center text-[10px] text-muted-foreground py-1 bg-muted">
                        {pageNum}
                      </div>
                    </div>
                  )
                )}
              </Document>
            </div>
          </div>
        )}

        {/* ── Main PDF Content ── */}
        <div
          ref={mainContentRef}
          className="flex-1 overflow-auto bg-muted"
          onScroll={handleMainScroll}
        >
          {/* Show loading spinner until PDF is loaded AND container is sized */}
          {(isLoading || !dimsReady) && (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <span className="text-muted-foreground text-sm">
                  Loading PDF…
                </span>
              </div>
            </div>
          )}

          <Document
            file={fileSource}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
          >
            {canRenderPages && (
              <div className="flex flex-col items-center py-4 gap-4">
                {Array.from({ length: numPages }, (_, i) => i + 1).map(
                  (pageNum) => (
                    <div
                      key={`page-${pageNum}`}
                      ref={(el) => {
                        if (el) pageRefs.current.set(pageNum, el);
                        else pageRefs.current.delete(pageNum);
                      }}
                      className="shadow-lg bg-white"
                    >
                      <Page
                        pageNumber={pageNum}
                        scale={fitMode === "custom" ? scale : undefined}
                        width={getPageWidth()}
                        height={getPageHeight()}
                        rotate={rotation}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    </div>
                  )
                )}
              </div>
            )}
          </Document>
        </div>
      </div>
    </div>
  );
};
